import { useEffect, useState } from "react";
import type { ImportHistoryBatch } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { fetchImportHistory } from "@/lib/import-api";

// TES-363 — reviewability of bulk content imports: the last ~50 batches with
// their per-batch created/updated/failed counts. NOTE the deliberate
// limitation (see ImportHistoryController): the batch audit metadata retains
// only the summary COUNTS — the per-item results array is shown to the
// importer at import time but not persisted, so this list cannot name which
// individual entity failed. The per-entity audit trail carries the granular
// record.

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const MODE_LABELS: Record<string, string> = {
  create: "Skapa",
  replace: "Ersätt",
};

function countChip(
  label: string,
  count: number,
  tone: "success" | "info" | "danger",
) {
  const cls =
    tone === "success"
      ? "bg-success/10 text-success border-success/30"
      : tone === "danger"
        ? "bg-danger/10 text-danger border-danger/30"
        : "bg-surfaceMuted text-textSecondary border-surfaceMuted";
  // A zero failed-count stays visible (it is the "all good" signal); zero
  // created/updated chips are noise and are dropped.
  if (count === 0 && tone !== "danger") return null;
  const toneCls =
    tone === "danger" && count === 0
      ? "bg-success/10 text-success border-success/30"
      : cls;
  return (
    <span
      key={label}
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${toneCls}`}
    >
      {count} {label}
    </span>
  );
}

export function ImportHistoryRoute() {
  const { signOut } = useAuth();
  const [batches, setBatches] = useState<ImportHistoryBatch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchImportHistory()
      .then((r) => setBatches(r.batches))
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Importhistorik</h1>
        <span className="text-sm text-textSecondary">
          {loading ? "Hämtar…" : batches ? `${batches.length} importer` : ""}
        </span>
      </div>

      <p className="text-sm text-textSecondary max-w-2xl">
        Varje rad är en körning av bulk-importen (JSON-paket eller CSV) med
        antal skapade, uppdaterade och misslyckade poster. Detaljer per post
        loggas i respektive innehålls ändringshistorik.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {batches && batches.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga importer har körts ännu.
        </div>
      ) : null}

      {batches && batches.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                <th className="text-left px-md py-sm font-medium">När</th>
                <th className="text-left px-md py-sm font-medium">Format</th>
                <th className="text-left px-md py-sm font-medium">Läge</th>
                <th className="text-left px-md py-sm font-medium">Poster</th>
                <th className="text-left px-md py-sm font-medium">Resultat</th>
                <th className="text-left px-md py-sm font-medium">Utförd av</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                >
                  <td className="px-md py-sm">{fmt(b.when)}</td>
                  <td className="px-md py-sm font-mono text-xs text-textSecondary uppercase">
                    {b.format ?? "—"}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {b.mode ? (MODE_LABELS[b.mode] ?? b.mode) : "—"}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {b.summary.total}
                  </td>
                  <td className="px-md py-sm">
                    <span className="flex flex-wrap gap-xs">
                      {countChip("skapade", b.summary.created, "success")}
                      {countChip("uppdaterade", b.summary.updated, "info")}
                      {countChip("misslyckade", b.summary.failed, "danger")}
                    </span>
                  </td>
                  <td
                    className="px-md py-sm font-mono text-xs text-textSecondary"
                    title={b.actorId ?? undefined}
                  >
                    {b.actorId ? `…${b.actorId.slice(-8)}` : "system"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
